import { useEffect, useId, useRef, useState } from "react";
import { Prompt } from "../components/Audio";
import Feedback from "../components/Feedback";
import { CheckIcon } from "../components/Icons";
import InkPad, { savedStrokesOf } from "../components/InkPad";
import { AnswerField, MathKeypad, numericKeysFor } from "../components/MathKeys";
import ui from "../components/ui.module.css";
import { isNumericRight } from "../data/activityAdapter";
import {
  decimalSeparator,
  entryTextOf,
  isNumericValue,
  parseNumericEntry,
} from "../data/maths/numeric";

const CELEBRATE_MS = 700;

const NAMES = {
  integer: "a whole number",
  decimal: "a decimal",
  fraction: "a fraction",
  percentage: "a percentage",
};
const listOf = (words) =>
  words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}`;

// Why an entry cannot be checked yet, in words a learner can act on.
const problemText = (reason, step) =>
  ({
    empty: "Type your answer first.",
    malformed: "We can't read that number. Check it and try again.",
    "zero-denominator": "A fraction can't have 0 at the bottom.",
    negative: "This answer is not a negative number.",
    representation: `Write your answer as ${listOf(step.representations.map((r) => NAMES[r]))}.`,
    unit: "Choose a unit.",
  })[reason];

// Compute, type, check. An incorrect answer stays editable and is not saved;
// only the accepted answer is (contract §3.3). Optional working uses the same
// ink surface as drawing: its strokes are kept as runtime state while the
// learner works, and a picture of meaningful working becomes protected
// evidence when the answer is accepted. Working never affects correctness.
const NumericEntryStep = ({ step, saved, onDone, onProgress, saving }) => {
  const separator = decimalSeparator();
  const accepted = isNumericValue(saved?.value) ? saved : null;
  const [text, setText] = useState(() => (accepted ? entryTextOf(accepted.value, separator) : ""));
  const [unit, setUnit] = useState(() =>
    step.unit ? step.unit.value : step.unitOptions?.some((u) => u.value === accepted?.unit) ? accepted.unit : null
  );
  const [problem, setProblem] = useState(null);
  const [result, setResult] = useState(null);
  const pad = useRef(null);
  const timer = useRef(null);
  const messageId = useId();

  useEffect(() => () => clearTimeout(timer.current), []);

  const edit = (next) => {
    setText(next);
    setProblem(null);
    setResult(null);
  };

  const check = async () => {
    if (result === "right" || saving) return;
    const parsed = parseNumericEntry(text, step);
    if (parsed.error) {
      setProblem(parsed.error);
      return;
    }
    if (step.unitOptions && !unit) {
      setProblem("unit");
      return;
    }
    const response = { value: parsed.value, ...(unit ? { unit } : {}) };
    if (!isNumericRight(step, response)) {
      setResult("wrong");
      return;
    }
    setResult("right");
    // Meaningful working only: no ink, no picture.
    const blob = step.working ? await pad.current?.exportPng() : null;
    timer.current = setTimeout(() => onDone(response, blob || null), CELEBRATE_MS);
  };

  const prefix = step.unit?.position === "prefix" ? step.unit.label : null;
  const suffix = step.unit?.position === "suffix" ? step.unit.label : null;
  const locked = saving || result === "right";

  const answer = (
    <div className={ui.numericAnswer}>
      <AnswerField
        value={text}
        onChange={edit}
        label="Your answer"
        prefix={prefix}
        suffix={suffix}
        invalid={Boolean(problem)}
        describedBy={messageId}
        disabled={locked}
      />
      {step.unitOptions && (
        <div className={ui.unitChoices} role="radiogroup" aria-label="Unit">
          {step.unitOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={unit === option.value}
              className={`${ui.unitChoice} ${unit === option.value ? ui.unitChosen : ""}`}
              onClick={() => {
                setUnit(option.value);
                setProblem(null);
                setResult(null);
              }}
              disabled={locked}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
      <MathKeypad
        value={text}
        onChange={edit}
        extraKeys={numericKeysFor(step)}
        disabled={locked}
        label="Number keys"
      />
      <p id={messageId} className={ui.mathProblem} role="alert">
        {problem ? problemText(problem, step) : ""}
      </p>
      <button
        type="button"
        className={`${ui.button} ${ui.large} ${ui.primary}`}
        onClick={check}
        disabled={locked}
      >
        <CheckIcon size={24} />
        {saving ? "Saving…" : "Check"}
      </button>
      <Feedback kind={result} />
    </div>
  );

  return (
    <>
      <Prompt step={step} />
      {step.working ? (
        <div className={ui.withWorking}>
          {answer}
          <section className={ui.working} aria-label="Working space">
            <h2 className={ui.workingTitle}>Working</h2>
            <InkPad
              ref={pad}
              template={step.working.template}
              saved={savedStrokesOf(saved?.working)}
              disabled={locked}
              label="Working space"
              className={ui.workingPad}
              onChange={(strokes) =>
                onProgress?.({ working: { format: "strokes-v1", strokes } })
              }
            />
          </section>
        </div>
      ) : (
        answer
      )}
    </>
  );
};

export default NumericEntryStep;
